import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import api from '../services/api';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import LoadingSpinner from './LoadingSpinner';
import Alert from './Alert';

const TextList = ({ userRole }) => {
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Usar useCallback para evitar recriação da função a cada renderização
  const fetchTexts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Buscando textos da API...');
      const response = await api.get('/textos');
      console.log('Textos recebidos:', response.data);
      setTexts(response.data);
    } catch (error) {
      console.error('Erro ao buscar textos:', error);
      setError('Falha ao carregar os textos');
      
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        navigate('/login');
      }
      setAlert({ message: 'Erro ao carregar textos. Tente novamente.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Usar useLayoutEffect para garantir que os dados sejam carregados antes da renderização
  useLayoutEffect(() => {
    console.log('TextList tornando-se visível - carregando dados imediatamente');
    fetchTexts();
  }, [fetchTexts, location.pathname]);

  // Manter o useEffect original como fallback, caso o useLayoutEffect não seja suficiente
  useEffect(() => {
    // Este efeito será executado após o useLayoutEffect
    console.log('TextList montado ou localização alterada (effect secundário)');
  }, []);

  // Componente se desmontou, exemplo de cleanup (se necessário)
  useEffect(() => {
    return () => {
      console.log('TextList desmontado');
    };
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este texto?')) {
      try {
        await api.delete(`/texto/${id}`);
        setAlert({ message: 'Texto excluído com sucesso!', type: 'success' });
        setTexts(texts.filter(text => text.id !== id));
      } catch (error) {
        console.error('Erro ao excluir texto:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('role');
          navigate('/login');
        }
        setAlert({ message: 'Erro ao excluir texto. Tente novamente.', type: 'error' });
      }
    }
  };

  const filteredTexts = texts.filter(text =>
    text.texto_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    text.conteudo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Verificar se temos dados
  const hasTexts = Array.isArray(texts) && texts.length > 0;
  console.log('Estado atual - textos:', hasTexts ? texts.length : 0, 'loading:', loading, 'erro:', error);

  // Função para lidar com o clique no card
  const handleCardClick = (text) => {
    // Navegar para a página de substituição com o ID do texto como parâmetro
    navigate(`/substituir?textoId=${text.id}`);
  };

  return (
    <div className="w-full">
      {alert && <Alert message={alert.message} type={alert.type} />}
      <div className="flex justify-between items-center mb-6">
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder="Pesquisar textos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg shadow focus:shadow-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder-gray-400 transition-all duration-200"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <button 
          onClick={fetchTexts}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow hover:shadow-md transition-all duration-200 flex items-center gap-2 ml-4"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Atualizar
        </button>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-400 dark:text-red-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-red-500 dark:text-red-400 text-lg">{error}</p>
          <button 
            onClick={fetchTexts}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow hover:shadow-md transition-all duration-200"
          >
            Tentar novamente
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTexts.length > 0 ? (
            filteredTexts.map((text) => (
              <motion.div
                key={text.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-100 dark:border-gray-700 shadow hover:translate-y-[-4px]"
                onClick={() => handleCardClick(text)}
              >
                <div className="relative">
                  <div className="absolute top-0 right-0 p-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      ID: {text.id}
                    </span>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-700 dark:from-blue-700 dark:to-blue-900 h-3 w-full"></div>
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-xl text-blue-600 dark:text-blue-400 truncate">{text.texto_id}</h3>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
                    <p className="text-gray-700 dark:text-gray-300 text-base line-clamp-3">{text.conteudo}</p>
                  </div>
                  <div className="flex flex-wrap mb-4">
                    {text.variaveis && text.variaveis.length > 0 ? (
                      text.variaveis.slice(0, 5).map((variavel, index) => (
                        <span key={index} className="inline-block bg-blue-100 dark:bg-blue-900 rounded-full px-3 py-1 text-sm font-medium text-blue-600 dark:text-blue-300 mr-2 mb-2 border border-blue-200 dark:border-blue-800">
                          @{variavel}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400 text-sm italic">Sem variáveis</span>
                    )}
                    {text.variaveis && text.variaveis.length > 5 && (
                      <span className="inline-block bg-gray-100 dark:bg-gray-700 rounded-full px-3 py-1 text-sm font-medium text-gray-600 dark:text-gray-300 mr-2 mb-2">
                        +{text.variaveis.length - 5} mais
                      </span>
                    )}
                  </div>
                  {userRole === 'admin' && (
                    <div className="flex justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
                      <Link 
                        to={`/editar/${text.id}`} 
                        className="bg-amber-500 hover:bg-amber-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center gap-2 shadow hover:shadow-md"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Editar
                      </Link>
                      <button 
                        onClick={() => handleDelete(text.id)}
                        className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center gap-2 shadow hover:shadow-md"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Excluir
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
              <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 dark:text-gray-600 mb-4 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400 text-lg font-medium mb-2">Nenhum texto encontrado</p>
                <p className="text-gray-400 dark:text-gray-500">Tente ajustar sua pesquisa ou adicione novos textos</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TextList;